import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Car, Wrench, AlertTriangle, CheckCircle, Edit2, Trash2, X, AlertOctagon, ArrowLeft, User, Search, Filter, LayoutGrid, List, Upload, QrCode, Download } from 'lucide-react';
import { differenceInDays, parseISO, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import type { Vehicle, VehicleBrand, VehicleModel } from '@shared/types';
import { getApiUrl } from '@/config';
import { useLayout } from '@shared/contexts/LayoutContext';
import { useAuth } from '@features/auth/context/AuthContext';
import { VehicleHistoryList } from './VehicleHistoryList';

interface Technician {
    id: string;
    name: string;
    // other fields ignored
}

// --- Utility Functions for Alerts ---

const getTechControlStatus = (validityDate?: string) => {
    if (!validityDate) return 'unknown';
    const daysLeft = differenceInDays(parseISO(validityDate), new Date());
    if (daysLeft < 0) return 'expired';
    if (daysLeft < 30) return 'warning';
    return 'ok';
};

const getServiceStatus = (currentKm: number, nextServiceKm?: number) => {
    if (!nextServiceKm) return 'unknown';
    const kmLeft = nextServiceKm - currentKm;
    if (kmLeft < 0) return 'expired';
    if (kmLeft < 1000) return 'warning';
    return 'ok';
};

// --- Modal Component ---

interface VehicleModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (vehicle: Partial<Vehicle>) => void; // Used for editing details
    vehicle?: Vehicle | null;
    technicians: Technician[];
    brands: VehicleBrand[];
    models: VehicleModel[];
    canWrite: boolean;
}

const VehicleModal: React.FC<VehicleModalProps> = ({ isOpen, onClose, onSave, vehicle, technicians, brands, models, canWrite }) => {
    const [activeTab, setActiveTab] = useState<'details' | 'actions' | 'history'>('details');

    // Form Data for Details Editing
    const [formData, setFormData] = useState<Partial<Vehicle>>({
        brand: '',
        model: '',
        licensePlate: '',
        fuelType: 'Diesel',
        year: new Date().getFullYear(),
        currentKm: 0,
        status: 'Disponible',
        notes: '',
        assignedTechId: '',
        techControlValidityDate: '',
        nextServiceKm: 0
    });

    // Action State
    const [actionType, setActionType] = useState<'status' | 'assign'>('status');
    const [actionStatus, setActionStatus] = useState<string>('Disponible');
    const [actionTech, setActionTech] = useState<string>('');
    const [actionNote, setActionNote] = useState<string>('');
    const [actionKm, setActionKm] = useState<number>(0);

    useEffect(() => {
        if (vehicle) {
            setFormData(vehicle);
            setActionKm(vehicle.currentKm);
            if (vehicle.status) setActionStatus(vehicle.status);
            if (vehicle.assignedTechId) setActionTech(vehicle.assignedTechId);
        } else {
            setFormData({
                brand: '',
                model: '',
                licensePlate: '',
                fuelType: 'Diesel',
                year: new Date().getFullYear(),
                currentKm: 0,
                status: 'Disponible',
                notes: '',
                assignedTechId: '',
                techControlValidityDate: '',
                nextServiceKm: 0
            });
            setActiveTab('details');
        }
    }, [vehicle, isOpen]);


    const handleDetailsSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(formData);
    };

    const handleActionSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!vehicle?._id) return;

        try {
            let endpoint = '';
            let body = {};

            if (actionType === 'assign') {
                if (actionTech) {
                    endpoint = `/api/vehicles/${vehicle._id}/assign`;
                    const tech = technicians.find(t => t.id === actionTech);
                    body = { technicianId: actionTech, technicianName: tech?.name || 'Inconnu' };
                } else {
                    endpoint = `/api/vehicles/${vehicle._id}/unassign`;
                }
            } else {
                endpoint = `/api/vehicles/${vehicle._id}/status`;
                body = { status: actionStatus, description: actionNote, km: actionKm };
            }

            const token = localStorage.getItem('token');
            const res = await fetch(getApiUrl(endpoint), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(body)
            });

            if (res.ok) {
                onClose();
                // Trigger refresh on parent? Parent should probably pass a refresh callback simpler than onSave logic
                // For now reuse onSave to trigger parent refresh effectively as onSave calls fetchData
                onSave({}); // Pass empty to just trigger refresh
            } else {
                alert("Erreur lors de l'action");
            }

        } catch (err) {
            console.error(err);
            alert("Erreur réseau");
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="bg-[#0f172a] border border-white/10 rounded-2xl p-6 w-full max-w-2xl shadow-2xl overflow-y-auto max-h-[90vh] flex flex-col"
            >
                <div className="flex justify-between items-center mb-6 border-b border-white/10 pb-4">
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <Car className="text-primary" />
                        {vehicle ? `Véhicule: ${vehicle.licensePlate}` : 'Nouveau Véhicule'}
                    </h2>

                    {vehicle && (
                        <div className="flex bg-white/5 p-1 rounded-lg gap-1">
                            <button
                                onClick={() => setActiveTab('details')}
                                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${activeTab === 'details' ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white'}`}
                            >
                                Détails
                            </button>
                            <button
                                onClick={() => setActiveTab('actions')}
                                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${activeTab === 'actions' ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white'}`}
                            >
                                Actions
                            </button>
                            <button
                                onClick={() => setActiveTab('history')}
                                className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${activeTab === 'history' ? 'bg-white/10 text-white' : 'text-gray-400 hover:text-white'}`}
                            >
                                Historique
                            </button>
                        </div>
                    )}

                    <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
                        <X size={24} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto pr-1">
                    {activeTab === 'details' ? (
                        <form onSubmit={handleDetailsSubmit} className="space-y-6">
                            {/* Identification */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <label className="text-xs font-semibold text-gray-400 uppercase">Marque</label>
                                    <select
                                        required
                                        value={formData.brand}
                                        onChange={e => setFormData({ ...formData, brand: e.target.value, model: '' })}
                                        className="w-full bg-[#1e293b] border border-white/10 rounded-lg px-3 py-2 text-white focus:border-primary focus:outline-none"
                                    >
                                        <option value="">-- Sélectionner --</option>
                                        {brands.map(b => (
                                            <option key={b._id} value={b.name}>{b.name}</option>
                                        ))}
                                        <option value="Autre">Autre</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-semibold text-gray-400 uppercase">Modèle</label>
                                    {formData.brand === 'Autre' ? (
                                        <input
                                            required
                                            type="text"
                                            value={formData.model}
                                            onChange={e => setFormData({ ...formData, model: e.target.value })}
                                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:border-primary focus:outline-none"
                                            placeholder="Saisir modèle"
                                        />
                                    ) : (
                                        <select
                                            required
                                            disabled={!formData.brand}
                                            value={formData.model}
                                            onChange={e => setFormData({ ...formData, model: e.target.value })}
                                            className="w-full bg-[#1e293b] border border-white/10 rounded-lg px-3 py-2 text-white focus:border-primary focus:outline-none disabled:opacity-50"
                                        >
                                            <option value="">-- Sélectionner --</option>
                                            {models.filter(m => m.brand === formData.brand).map(m => (
                                                <option key={m._id} value={m.name}>{m.name}</option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-semibold text-gray-400 uppercase">Immatriculation</label>
                                    <input
                                        required
                                        type="text"
                                        value={formData.licensePlate}
                                        onChange={e => setFormData({ ...formData, licensePlate: e.target.value.toUpperCase() })}
                                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:border-primary focus:outline-none font-mono"
                                    />
                                </div>
                            </div>

                            {/* Specs */}
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="space-y-2">
                                    <label className="text-xs font-semibold text-gray-400 uppercase">Type Carburant</label>
                                    <select
                                        value={formData.fuelType}
                                        onChange={e => setFormData({ ...formData, fuelType: e.target.value as any })}
                                        className="w-full bg-[#1e293b] border border-white/10 rounded-lg px-3 py-2 text-white focus:border-primary focus:outline-none"
                                    >
                                        <option value="Diesel">Diesel</option>
                                        <option value="Essence">Essence</option>
                                        <option value="Électrique">Électrique</option>
                                        <option value="Hybride">Hybride</option>
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-semibold text-gray-400 uppercase">Année</label>
                                    <input
                                        type="number"
                                        value={formData.year}
                                        onChange={e => setFormData({ ...formData, year: parseInt(e.target.value) })}
                                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:border-primary focus:outline-none"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-semibold text-gray-400 uppercase">Km Actuel</label>
                                    <input
                                        type="number"
                                        value={formData.currentKm}
                                        onChange={e => setFormData({ ...formData, currentKm: parseInt(e.target.value) })}
                                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:border-primary focus:outline-none"
                                    />
                                </div>
                            </div>

                            {/* Maintenance */}
                            <div className="p-4 bg-white/5 rounded-xl border border-white/5 space-y-4">
                                <h3 className="text-sm font-bold text-gray-300 flex items-center gap-2">
                                    <Wrench size={16} /> Suivi Maintenance
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className="text-xs text-gray-400">Date validité Ctrl Tech.</label>
                                        <input
                                            type="date"
                                            value={formData.techControlValidityDate ? format(parseISO(formData.techControlValidityDate), 'yyyy-MM-dd') : ''}
                                            onChange={e => setFormData({ ...formData, techControlValidityDate: e.target.value })}
                                            className="w-full bg-[#0f172a] border border-white/10 rounded-lg px-3 py-2 text-white focus:border-primary focus:outline-none"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <label className="text-xs text-gray-400">Prochaine Vidange (km)</label>
                                        <input
                                            type="number"
                                            placeholder="Ex: 150000"
                                            value={formData.nextServiceKm || ''}
                                            onChange={e => setFormData({ ...formData, nextServiceKm: parseInt(e.target.value) })}
                                            className="w-full bg-[#0f172a] border border-white/10 rounded-lg px-3 py-2 text-white focus:border-primary focus:outline-none"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Read-only status in details view if editing */}
                            {vehicle && (
                                <div className="bg-white/5 p-4 rounded-lg flex justify-between items-center text-sm text-gray-400">
                                    <span>Statut Actuel: <span className="text-white font-medium">{formData.status}</span></span>
                                    <span>Assigné à: <span className="text-white font-medium">{technicians.find(t => t.id === formData.assignedTechId)?.name || 'Personne'}</span></span>
                                </div>
                            )}

                            {canWrite && (
                                <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                                    <button type="submit" className="bg-primary hover:bg-primary/90 text-white px-6 py-2 rounded-lg font-medium shadow-lg transition-all">
                                        Enregistrer les modifications
                                    </button>
                                </div>
                            )}
                        </form>
                    ) : activeTab === 'actions' ? (
                        <div className="space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                                <button
                                    onClick={() => setActionType('status')}
                                    className={`p-4 rounded-xl border transition-all text-center ${actionType === 'status' ? 'bg-purple-500/20 border-purple-500 text-purple-200' : 'bg-white/5 border-white/10 text-gray-400'}`}
                                >
                                    <div className="font-bold mb-1">Changer Statut</div>
                                    <div className="text-xs opacity-70">Mise à jour état, maintenance...</div>
                                </button>
                                <button
                                    onClick={() => setActionType('assign')}
                                    className={`p-4 rounded-xl border transition-all text-center ${actionType === 'assign' ? 'bg-blue-500/20 border-blue-500 text-blue-200' : 'bg-white/5 border-white/10 text-gray-400'}`}
                                >
                                    <div className="font-bold mb-1">Assignation</div>
                                    <div className="text-xs opacity-70">Confier ou reprendre le véhicule</div>
                                </button>
                            </div>

                            <form onSubmit={handleActionSubmit} className="space-y-4">
                                {actionType === 'status' && (
                                    <>
                                        <div className="space-y-2">
                                            <label className="text-xs font-semibold text-gray-400 uppercase">Nouveau Statut</label>
                                            <select
                                                value={actionStatus}
                                                onChange={e => setActionStatus(e.target.value)}
                                                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:border-primary focus:outline-none"
                                            >
                                                <option value="Disponible">Disponible</option>
                                                <option value="En mission">En mission</option>
                                                <option value="En réparation">En réparation</option>
                                                <option value="Hors service">Hors service</option>
                                            </select>
                                        </div>
                                    </>
                                )}

                                {actionType === 'assign' && (
                                    <div className="space-y-2">
                                        <label className="text-xs font-semibold text-gray-400 uppercase">Technicien</label>
                                        <select
                                            value={actionTech}
                                            onChange={e => setActionTech(e.target.value)}
                                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:border-primary focus:outline-none"
                                        >
                                            <option value="">-- Restituer (Aucun) --</option>
                                            {technicians.map(t => (
                                                <option key={t.id} value={t.id}>{t.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <label className="text-xs font-semibold text-gray-400 uppercase">Relevé Kilométrique</label>
                                    <input
                                        type="number"
                                        value={actionKm}
                                        onChange={e => setActionKm(parseInt(e.target.value))}
                                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:border-primary focus:outline-none"
                                    />
                                    <p className="text-[10px] text-gray-500">Mettez à jour si différent du précédent ({vehicle?.currentKm} km)</p>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-xs font-semibold text-gray-400 uppercase">
                                        {actionStatus === 'En réparation' ? 'Cause / Détails Maintenance' : 'Note / Raison'}
                                    </label>
                                    <textarea
                                        value={actionNote}
                                        onChange={e => setActionNote(e.target.value)}
                                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:border-primary focus:outline-none min-h-[80px]"
                                        placeholder={actionStatus === 'En réparation' ? "Ex: Panne embrayage, RDV Garage X..." : "Optionnel..."}
                                        required={actionStatus === 'En réparation'}
                                    />
                                </div>

                                {canWrite && (
                                    <button type="submit" className="w-full bg-primary hover:bg-primary/90 text-white px-6 py-2 rounded-lg font-medium shadow-lg transition-all">
                                        Valider l'Action
                                    </button>
                                )}
                            </form>
                        </div>
                    ) : (
                        <VehicleHistoryList vehicleId={vehicle?._id || ''} />
                    )}
                </div>
            </motion.div>
        </div>
    );
};

export const FleetView = () => {
    const navigate = useNavigate();
    const { layoutMode } = useLayout();
    const { user } = useAuth();
    
    const canWrite = user?.role === 'admin' || 
                    (user?.permissions?.fleet === 'write') || 
                    (user?.role === 'planner' && !user?.permissions);
    const [vehicles, setVehicles] = useState<Vehicle[]>([]);
    const [technicians, setTechnicians] = useState<Technician[]>([]);
    const [brands, setBrands] = useState<VehicleBrand[]>([]);
    const [models, setModels] = useState<VehicleModel[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all'); // all, available, assigned, maintenance, lost
    const [technicianFilter, setTechnicianFilter] = useState('all');
    const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');

    // Maintain compatibility with existing filter state if needed, or replace. 
    // We will replace 'filter' with 'statusFilter' for the main list.
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingVehicle, setEditingVehicle] = useState<Vehicle | null>(null);
    const [error, setError] = useState<string | null>(null);

    const handleExportCSV = () => {
        const headers = ['Marque', 'Modèle', 'Immatriculation', 'Carburant', 'Année', 'Kilométrage', 'Statut', 'Assigné à'];
        const csvRows = vehicles.map(v => {
            const tech = technicians.find(t => t.id === v.assignedTechId);
            return [
                v.brand,
                v.model,
                v.licensePlate,
                v.fuelType,
                v.year,
                v.currentKm,
                v.status,
                tech ? tech.name : 'Aucun'
            ].map(val => `"${val}"`).join(',');
        });

        const csvContent = [headers.join(','), ...csvRows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        link.setAttribute('download', `flotte_vehicules_${format(new Date(), 'yyyy-MM-dd')}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);

            const token = localStorage.getItem('token');
            const headers = { 'Authorization': `Bearer ${token}` };

            // Parallel fetch
            const [vehRes, techRes, brandRes, modelRes] = await Promise.all([
                fetch(getApiUrl('/api/vehicles'), { headers }),
                fetch(getApiUrl('/api/technicians'), { headers }),
                fetch(getApiUrl('/api/vehicle-brands'), { headers }),
                fetch(getApiUrl('/api/vehicle-models'), { headers })
            ]);

            if (vehRes.ok) {
                const vData = await vehRes.json();
                setVehicles(vData);
            } else {
                throw new Error('Impossible de charger les véhicules');
            }

            if (techRes.ok) {
                const tData = await techRes.json();
                setTechnicians(tData);
            }

            if (brandRes.ok) {
                const bData = await brandRes.json();
                setBrands(bData);
            }

            if (modelRes.ok) {
                const mData = await modelRes.json();
                setModels(mData);
            }

        } catch (err) {
            console.error(err);
            setError('Erreur de connexion au serveur.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();

        const handleVehicleUpdate = () => {
            console.log('FleetView: Received vehicle update event');
            fetchData();
        };

        window.addEventListener('vehicles_updated', handleVehicleUpdate);
        return () => window.removeEventListener('vehicles_updated', handleVehicleUpdate);
    }, []);

    const handleSave = async (vehicleData: Partial<Vehicle>) => {
        try {
            if (Object.keys(vehicleData).length === 0) {
                // Just refresh
                fetchData();
                return;
            }

            const method = editingVehicle ? 'PUT' : 'POST';
            const endpoint = editingVehicle ? `/api/vehicles/${editingVehicle._id}` : '/api/vehicles';

            const token = localStorage.getItem('token');
            const res = await fetch(getApiUrl(endpoint), {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(vehicleData)
            });

            if (res.ok) {
                fetchData(); // Refresh everything
                setIsModalOpen(false);
                setEditingVehicle(null);
            } else {
                const err = await res.json();
                alert(err.error || 'Erreur lors de la sauvegarde');
            }
        } catch (e) {
            console.error(e);
            alert('Erreur réseau');
        }
    };

    const handleDelete = async (id?: string) => {
        if (!id || !confirm('Êtes-vous sûr de vouloir supprimer ce véhicule ?')) return;
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(getApiUrl(`/api/vehicles/${id}`), {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) fetchData();
        } catch (e) { console.error(e); }
    };

    const [importing, setImporting] = useState(false);

    const handleImportCSV = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files || e.target.files.length === 0) return;
        const file = e.target.files[0];
        const reader = new FileReader();

        setImporting(true);
        reader.onload = async (event) => {
            try {
                const text = event.target?.result as string;
                if (!text) return;

                const lines = text.split('\n');
                const headers = lines[0].split(',').map(h => h.trim().toLowerCase());

                // Expected headers roughly: brand, model, licensePlate, year, currentKm, fuelType
                // We will try to map loosely

                let successCount = 0;
                let failCount = 0;

                for (let i = 1; i < lines.length; i++) {
                    const line = lines[i].trim();
                    if (!line) continue;

                    const values = line.split(',');
                    if (values.length < 3) { // Min requirements
                        failCount++;
                        continue;
                    }

                    const vehicleData: any = {
                        status: 'Disponible'
                    };

                    headers.forEach((header, index) => {
                        const value = values[index]?.trim();
                        if (!value) return;

                        if (header.includes('brand') || header.includes('marque')) vehicleData.brand = value;
                        else if (header.includes('model') || header.includes('modèle')) vehicleData.model = value;
                        else if (header.includes('plate') || header.includes('plaque') || header.includes('immatriculation')) vehicleData.licensePlate = value;
                        else if (header.includes('year') || header.includes('année')) vehicleData.year = parseInt(value);
                        else if (header.includes('km') || header.includes('kilométrage')) vehicleData.currentKm = parseInt(value);
                        else if (header.includes('fuel') || header.includes('carburant')) vehicleData.fuelType = value;
                    });

                    // Validation basics
                    if (!vehicleData.brand || !vehicleData.model || !vehicleData.licensePlate) {
                        failCount++;
                        console.warn('Skipping invalid line:', line);
                        continue;
                    }

                    // Defaults
                    if (!vehicleData.year) vehicleData.year = new Date().getFullYear();
                    if (!vehicleData.currentKm) vehicleData.currentKm = 0;
                    if (!vehicleData.fuelType) vehicleData.fuelType = 'Diesel';

                    // API Call
                    try {
                        const token = localStorage.getItem('token');
                        const res = await fetch(getApiUrl('/api/vehicles'), {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`
                            },
                            body: JSON.stringify(vehicleData)
                        });
                        if (res.ok) successCount++;
                        else failCount++;
                    } catch (err) {
                        failCount++;
                    }
                }

                alert(`Import terminé : ${successCount} ajoutés, ${failCount} échoués.`);
                fetchData();

            } catch (err) {
                console.error(err);
                alert('Erreur lors de la lecture du fichier CSV');
            } finally {
                setImporting(false);
                // Reset input
                e.target.value = '';
            }
        };

        reader.readAsText(file);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Disponible': return 'bg-green-500/10 text-green-400 border-green-500/20';
            case 'En mission': return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
            case 'En réparation': return 'bg-orange-500/10 text-orange-400 border-orange-500/20';
            case 'Hors service': return 'bg-red-500/10 text-red-400 border-red-500/20';
            default: return 'bg-gray-500/10 text-gray-400';
        }
    };

    const getTechName = (techId?: string) => {
        if (!techId) return null;
        const tech = technicians.find(t => t.id === techId);
        return tech ? tech.name : 'Inconnu';
    };

    const filteredVehicles = vehicles.filter(v => {
        // Search Filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            const matchesSearch =
                v.brand.toLowerCase().includes(query) ||
                v.model.toLowerCase().includes(query) ||
                v.licensePlate.toLowerCase().includes(query);
            if (!matchesSearch) return false;
        }

        // Technician Filter
        if (technicianFilter !== 'all') {
            if (v.assignedTechId !== technicianFilter) return false;
        }

        // Status Filter
        if (statusFilter === 'all') return true;
        if (statusFilter === 'available') return v.status === 'Disponible';
        if (statusFilter === 'assigned') return v.status === 'En mission';
        if (statusFilter === 'maintenance') return v.status === 'En réparation';
        if (statusFilter === 'lost') return v.status === 'Hors service';
        if (statusFilter === 'alerts') {
            const techStatus = getTechControlStatus(v.techControlValidityDate);
            const serviceStatus = getServiceStatus(v.currentKm, v.nextServiceKm);
            return techStatus !== 'ok' || serviceStatus !== 'ok';
        }

        return true;
    });

    const stats = {
        total: vehicles.length,
        assigned: vehicles.filter(v => v.status === 'En mission').length,
        maintenance: vehicles.filter(v => v.status === 'En réparation').length,
        alerts: vehicles.filter(v => {
            const techStatus = getTechControlStatus(v.techControlValidityDate);
            const serviceStatus = getServiceStatus(v.currentKm, v.nextServiceKm);
            return techStatus !== 'ok' || serviceStatus !== 'ok';
        }).length
    };

    return (
        <div className="min-h-screen p-6 md:p-8 max-w-7xl mx-auto space-y-8 pb-24">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    {layoutMode !== 'sidebar' && (
                        <button
                            onClick={() => navigate('/')}
                            className="p-2 hover:bg-glass-border/10 rounded-full transition-colors text-muted hover:text-main"
                        >
                            <ArrowLeft size={24} />
                        </button>
                    )}
                    <div>
                        <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                            <Car className="text-primary" />
                            Gestion de Flotte
                        </h1>
                        <p className="text-gray-400 mt-1">Suivi des véhicules et maintenance</p>
                    </div>
                </div>

                <div className="flex gap-2">
                    <button
                        onClick={handleExportCSV}
                        className="glass-button bg-blue-500/10 text-blue-400 border-blue-500/50 hover:bg-blue-500/20 flex items-center gap-2 text-sm px-4 py-2 rounded-xl transition-colors"
                        title="Exporter en CSV"
                    >
                        <Download size={18} />
                        <span className="hidden sm:inline">Exporter CSV</span>
                    </button>

                    <button className="flex items-center gap-2 px-4 py-2 rounded-xl border border-white/10 bg-[#0f172a]/40 text-gray-300 hover:bg-white/5 transition-colors">
                        <QrCode size={18} />
                        <span className="hidden sm:inline">Scan QR</span>
                    </button>

                    {canWrite && (
                        <>
                            <label className={`flex items-center gap-2 px-4 py-2 rounded-xl border border-white/10 transition-colors cursor-pointer ${importing ? 'bg-gray-700/50 text-gray-400' : 'bg-[#0f172a]/40 text-gray-300 hover:bg-white/5'}`}>
                                <Upload size={18} className={importing ? "animate-bounce" : ""} />
                                <span>{importing ? "Import..." : "Importer CSV"}</span>
                                <input
                                    type="file"
                                    accept=".csv"
                                    className="hidden"
                                    onChange={handleImportCSV}
                                    disabled={importing}
                                />
                            </label>

                            <button
                                onClick={() => { setEditingVehicle(null); setIsModalOpen(true); }}
                                className="flex items-center gap-2 bg-[#0f172a]/40 hover:bg-white/5 text-white px-4 py-2 rounded-xl border border-white/10 transition-colors"
                            >
                                <Plus size={18} /> Nouveau Véhicule
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                    { label: 'Total Véhicules', value: stats.total, color: 'text-white', border: 'border-glass-border' },
                    { label: 'En Mission', value: stats.assigned, color: 'text-blue-400', border: 'border-blue-500/30' },
                    { label: 'En Maintenance', value: stats.maintenance, color: 'text-amber-400', border: 'border-amber-500/30' },
                    { label: 'Alertes', value: stats.alerts, color: 'text-red-400', border: 'border-red-500/30' },
                ].map((stat, idx) => (
                    <div key={idx} className={`glass-panel p-4 flex flex-col items-center justify-center border ${stat.border}`}>
                        <span className={`text-2xl font-bold ${stat.color}`}>{stat.value}</span>
                        <span className="text-xs text-muted font-medium uppercase tracking-wide">{stat.label}</span>
                    </div>
                ))}
            </div>
            {/* ... */}


            {/* Toolbar */}
            <div className="glass-panel p-4 flex flex-col md:flex-row gap-4 items-center justify-between">
                {/* Search */}
                <div className="relative w-full md:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={16} />
                    <input
                        type="text"
                        placeholder="Rechercher un véhicule..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="glass-input w-full !pl-9 py-2 text-sm"
                    />
                </div>

                <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0 no-scrollbar">
                    <Filter className="text-muted mr-2 hidden md:block" size={16} />

                    {/* Status Tabs */}
                    {[
                        { id: 'all', label: 'Tout' },
                        { id: 'available', label: 'Dispo' },
                        { id: 'assigned', label: 'Sortis' },
                        { id: 'maintenance', label: 'Maint.' },
                        { id: 'lost', label: 'Perdu' },
                        { id: 'alerts', label: 'Alertes' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setStatusFilter(tab.id)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors border ${statusFilter === tab.id
                                ? 'bg-white/10 text-white border-white/20'
                                : 'bg-glass-border/10 text-muted border-transparent hover:bg-glass-border/20'
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}

                    <div className="w-px h-6 bg-glass-border mx-2 hidden md:block"></div>

                    {/* Technician Dropdown */}
                    <select
                        value={technicianFilter}
                        onChange={(e) => setTechnicianFilter(e.target.value)}
                        className="glass-input py-1.5 pl-2 pr-8 text-xs min-w-[120px]"
                    >
                        <option value="all">Tous techniciens</option>
                        {technicians.map(tech => (
                            <option key={tech.id} value={tech.id}>{tech.name}</option>
                        ))}
                    </select>

                    <div className="w-px h-6 bg-glass-border mx-2 hidden md:block"></div>

                    {/* View Mode Toggle */}
                    <div className="flex bg-glass-border/10 p-1 rounded-lg border border-glass-border/50">
                        <button
                            onClick={() => setViewMode('grid')}
                            className={`p-1.5 rounded-md transition-all ${viewMode === 'grid' ? 'bg-white/10 text-white shadow-sm' : 'text-muted hover:text-gray-300'}`}
                            title="Vue Grille"
                        >
                            <LayoutGrid size={16} />
                        </button>
                        <button
                            onClick={() => setViewMode('list')}
                            className={`p-1.5 rounded-md transition-all ${viewMode === 'list' ? 'bg-white/10 text-white shadow-sm' : 'text-muted hover:text-gray-300'}`}
                            title="Vue Liste"
                        >
                            <List size={16} />
                        </button>
                    </div>
                </div>
            </div>

            {/* List */}
            {/* Grid vs List View */}
            {
                viewMode === 'grid' ? (
                    /* Grid View */
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                        {loading ? (
                            <div className="col-span-full py-12 text-center text-gray-400 animate-pulse">
                                Chargement des données...
                            </div>
                        ) : filteredVehicles.length === 0 ? (
                            <div className="col-span-full py-12 text-center text-gray-500">
                                Aucun véhicule trouvé
                            </div>
                        ) : (
                            filteredVehicles.map(vehicle => {
                                const techStatus = getTechControlStatus(vehicle.techControlValidityDate);
                                const serviceStatus = getServiceStatus(vehicle.currentKm, vehicle.nextServiceKm);
                                const assignedTechName = getTechName(vehicle.assignedTechId);

                                return (
                                    <div key={vehicle._id} className="glass-panel p-4 rounded-xl border border-glass-border hover:border-primary/30 transition-all group flex flex-col gap-3">
                                        <div className="flex justify-between items-start">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center text-primary">
                                                    <Car size={20} />
                                                </div>
                                                <div>
                                                    <div className="font-bold text-white">{vehicle.brand} {vehicle.model}</div>
                                                    <div className="text-xs text-gray-400 font-mono">{vehicle.licensePlate}</div>
                                                </div>
                                            </div>
                                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${getStatusColor(vehicle.status)}`}>
                                                {vehicle.status}
                                            </span>
                                        </div>

                                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-400 mt-2">
                                            <div className="bg-white/5 p-2 rounded">
                                                <div className="uppercase opacity-70 text-[10px]">Année</div>
                                                <div className="text-white font-medium">{vehicle.year}</div>
                                            </div>
                                            <div className="bg-white/5 p-2 rounded">
                                                <div className="uppercase opacity-70 text-[10px]">Kilométrage</div>
                                                <div className="text-white font-medium">{vehicle.currentKm.toLocaleString()}</div>
                                            </div>
                                            <div className="bg-white/5 p-2 rounded">
                                                <div className="uppercase opacity-70 text-[10px]">Carburant</div>
                                                <div className="text-white font-medium">{vehicle.fuelType}</div>
                                            </div>

                                            {assignedTechName ? (
                                                <div className="bg-white/5 p-2 rounded flex flex-col justify-center">
                                                    <div className="uppercase opacity-70 text-[10px]">Assigné à</div>
                                                    <div className="text-white font-medium truncate">{assignedTechName}</div>
                                                </div>
                                            ) : (
                                                <div className="bg-white/5 p-2 rounded flex items-center justify-center opacity-50">
                                                    -
                                                </div>
                                            )}
                                        </div>

                                        {/* Warnings */}
                                        {(techStatus !== 'ok' || serviceStatus !== 'ok') && (
                                            <div className="bg-red-500/10 border border-red-500/20 rounded p-2 text-xs space-y-1 mt-1">
                                                {techStatus !== 'ok' && (
                                                    <div className="flex items-center gap-1.5 text-red-400">
                                                        <AlertOctagon size={12} />
                                                        <span>CT: {vehicle.techControlValidityDate ? format(parseISO(vehicle.techControlValidityDate), 'dd/MM/yyyy') : 'N/A'}</span>
                                                    </div>
                                                )}
                                                {serviceStatus !== 'ok' && (
                                                    <div className="flex items-center gap-1.5 text-orange-400">
                                                        <Wrench size={12} />
                                                        <span>Vidange: {vehicle.nextServiceKm}km</span>
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        <div className="mt-auto pt-3 flex justify-end gap-2 border-t border-white/5">
                                            <button
                                                onClick={() => { setEditingVehicle(vehicle); setIsModalOpen(true); }}
                                                className="px-3 py-1.5 bg-white/5 hover:bg-white/10 rounded-lg text-xs font-medium text-white transition-colors"
                                            >
                                                Gérer / Modifier
                                            </button>
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                ) : (
                    /* List View */
                    <div className="glass-panel rounded-2xl border border-glass-border overflow-hidden">
                        {/* ... existing table code ... */}
                        <div className="overflow-x-auto">
                            {loading ? (
                                <div className="flex justify-center items-center py-12 text-gray-400 animate-pulse">
                                    Chargement des données...
                                </div>
                            ) : error ? (
                                <div className="p-8 text-center bg-red-500/10 border border-red-500/20 m-4 rounded-xl">
                                    <AlertTriangle className="mx-auto mb-2 text-red-400" size={32} />
                                    <p className="text-red-400 font-medium">{error}</p>
                                    <button onClick={fetchData} className="mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm transition-colors">
                                        Réessayer
                                    </button>
                                </div>
                            ) : (
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b border-glass-border bg-white/5">
                                            <th className="p-4 text-xs font-semibold text-gray-400 uppercase">Véhicule</th>
                                            <th className="p-4 text-xs font-semibold text-gray-400 uppercase">Plaque</th>
                                            <th className="p-4 text-xs font-semibold text-gray-400 uppercase">Kilométrage</th>
                                            <th className="p-4 text-xs font-semibold text-gray-400 uppercase">Statut</th>
                                            <th className="p-4 text-xs font-semibold text-gray-400 uppercase">Maintenance</th>
                                            <th className="p-4 text-xs font-semibold text-gray-400 uppercase text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-glass-border">
                                        {filteredVehicles.length === 0 ? (
                                            <tr>
                                                <td colSpan={6} className="p-8 text-center text-gray-500">Aucun véhicule trouvé</td>
                                            </tr>
                                        ) : (
                                            filteredVehicles.map(vehicle => {
                                                const techStatus = getTechControlStatus(vehicle.techControlValidityDate);
                                                const serviceStatus = getServiceStatus(vehicle.currentKm, vehicle.nextServiceKm);
                                                const assignedTechName = getTechName(vehicle.assignedTechId);

                                                return (
                                                    <tr key={vehicle._id} className="hover:bg-white/5 transition-colors group">
                                                        <td className="p-4">
                                                            <div className="font-medium text-white">{vehicle.brand} {vehicle.model}</div>
                                                            <div className="text-xs text-gray-500">{vehicle.fuelType} - {vehicle.year}</div>
                                                        </td>
                                                        <td className="p-4 font-mono text-sm text-gray-300">
                                                            {vehicle.licensePlate}
                                                        </td>
                                                        <td className="p-4 text-sm text-gray-300">
                                                            {vehicle.currentKm.toLocaleString()} km
                                                        </td>
                                                        <td className="p-4">
                                                            <div className="flex flex-col gap-2 items-start">
                                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(vehicle.status)}`}>
                                                                    {vehicle.status}
                                                                </span>
                                                                {assignedTechName && (
                                                                    <div className="flex items-center gap-1 text-xs text-gray-400 bg-white/5 px-2 py-1 rounded">
                                                                        <User size={12} />
                                                                        {assignedTechName}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="p-4">
                                                            <div className="flex flex-col gap-1">
                                                                {techStatus !== 'ok' && (
                                                                    <div className={`flex items-center gap-1.5 text-xs ${techStatus === 'expired' ? 'text-red-400' : 'text-orange-400'}`}>
                                                                        <AlertOctagon size={12} />
                                                                        <span>Ctrl Tech: {vehicle.techControlValidityDate ? format(parseISO(vehicle.techControlValidityDate), 'dd/MM/yyyy', { locale: fr }) : 'N/A'}</span>
                                                                    </div>
                                                                )}
                                                                {serviceStatus !== 'ok' && (
                                                                    <div className={`flex items-center gap-1.5 text-xs ${serviceStatus === 'expired' ? 'text-red-400' : 'text-orange-400'}`}>
                                                                        <Wrench size={12} />
                                                                        <span>Vidange: {vehicle.nextServiceKm}km</span>
                                                                    </div>
                                                                )}
                                                                {techStatus === 'ok' && serviceStatus === 'ok' && (
                                                                    <span className="text-xs text-green-500 flex items-center gap-1"><CheckCircle size={12} /> RAS</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                        <td className="p-4 text-right">
                                                            {canWrite && (
                                                                <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                                    <button
                                                                        onClick={() => { setEditingVehicle(vehicle); setIsModalOpen(true); }}
                                                                        className="p-2 hover:bg-white/10 rounded-lg text-gray-400 hover:text-white transition-colors"
                                                                    >
                                                                        <Edit2 size={16} />
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleDelete(vehicle._id)}
                                                                        className="p-2 hover:bg-red-500/20 rounded-lg text-gray-400 hover:text-red-400 transition-colors"
                                                                    >
                                                                        <Trash2 size={16} />
                                                                    </button>
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                )}


            <AnimatePresence>
                {isModalOpen && (
                    <VehicleModal
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        onSave={handleSave}
                        vehicle={editingVehicle}
                        technicians={technicians}
                        brands={brands}
                        models={models}
                        canWrite={canWrite}
                    />
                )}
            </AnimatePresence>
        </div >
    );
};
